import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import QuoteModal from "./QuoteModal";
import { PACKAGES } from "@/data";
import { useBooking } from "@/components/booking/BookingContext";

export default function Pricing() {
  const { openBooking } = useBooking();
  const [quotePkg, setQuotePkg] = useState("");
  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="py-24 md:py-36"
      style={{ backgroundColor: "var(--bg-primary)" }}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p
              className="text-sm tracking-[0.28em] uppercase mb-5"
              style={{ color: "var(--rose-mocha-deep)" }}
            >
              The Packages
            </p>
            <h2
              className="font-serif text-4xl md:text-5xl leading-tight tracking-tight"
              style={{ color: "var(--text-primary)" }}
            >
              Thoughtfully curated support, tailored to you.
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {PACKAGES.map((p, i) => {
            const dark = p.highlight;
            return (
              <motion.div
                key={p.name}
                data-testid={`package-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-3xl p-8 md:p-10 flex flex-col transition-transform duration-500 hover:-translate-y-2"
                style={{
                  backgroundColor: dark ? "var(--mocha-brown)" : "var(--bg-secondary)",
                  border: dark
                    ? "1px solid var(--accent-gold)"
                    : "1px solid var(--border-light)",
                  boxShadow: dark ? "0 30px 60px -30px rgba(74,59,50,0.55)" : "none",
                }}
              >
                {dark && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs tracking-[0.2em] uppercase px-4 py-1.5 rounded-full"
                    style={{ backgroundColor: "var(--accent-gold)", color: "#2E241D" }}
                  >
                    Most Loved
                  </span>
                )}
                <p
                  className="text-sm tracking-[0.2em] uppercase mb-2"
                  style={{ color: dark ? "var(--accent-gold-soft)" : "var(--rose-mocha-deep)" }}
                >
                  {p.subtitle}
                </p>
                <h3
                  className="font-serif text-3xl md:text-4xl tracking-tight mb-8"
                  style={{ color: dark ? "#FBF6F3" : "var(--text-primary)" }}
                >
                  {p.name}
                </h3>

                <ul className="flex flex-col gap-4 mb-10 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 shrink-0 grid place-items-center w-5 h-5 rounded-full"
                        style={{
                          backgroundColor: dark
                            ? "var(--accent-gold)"
                            : "var(--rose-mocha)",
                        }}
                      >
                        <Check size={12} color="#FBF6F3" strokeWidth={3} />
                      </span>
                      <span
                        className="text-sm md:text-base leading-snug"
                        style={{
                          color: dark ? "rgba(251,246,243,0.9)" : "var(--text-secondary)",
                        }}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={openBooking}
                  data-testid={`package-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex justify-center items-center rounded-full px-6 py-3.5 text-sm font-medium transition-colors duration-300"
                  style={{
                    backgroundColor: dark ? "var(--accent-gold)" : "var(--mocha-brown)",
                    color: dark ? "#2E241D" : "#FBF6F3",
                  }}
                >
                  Contact Me
                </button>
                <button
                  onClick={() => setQuotePkg(p.name)}
                  data-testid={`package-quote-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-3 inline-flex justify-center items-center rounded-full px-6 py-3.5 text-sm font-medium transition-opacity duration-300 hover:opacity-80"
                  style={{
                    border: dark ? "1px solid var(--accent-gold)" : "1px solid var(--mocha-brown)",
                    color: dark ? "var(--accent-gold-soft)" : "var(--mocha-brown)",
                  }}
                >
                  Request a Quote
                </button>
              </motion.div>
            );
          })}
        </div>

        <Reveal>
          <p
            data-testid="pricing-addons-note"
            className="text-center text-base md:text-lg leading-relaxed max-w-2xl mx-auto mt-14"
            style={{ color: "var(--text-secondary)" }}
          >
            Need something more specific? We also offer{" "}
            <span style={{ color: "var(--rose-mocha-deep)" }}>tailored add-ons</span> and fully
            customized packages built around your business.
          </p>
        </Reveal>
      </div>
      <QuoteModal open={!!quotePkg} initialPackage={quotePkg} onClose={() => setQuotePkg("")} />
    </section>
  );
}
